import React, { useState, useEffect, useRef } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

export interface MenuOption {
  label: string;
  onClick: () => void;
  // Optional richer content: when provided the option renders a title + subtitle
  // layout instead of the plain label button.
  subtitle?: React.ReactNode;
  // Set false for an informational row that isn't itself actionable.
  clickable?: boolean;
  // Grey the title to signal an unavailable/disabled option.
  titleMuted?: boolean;
  // Optional icon rendered next to the title (e.g. a lock for a locked option).
  titleIcon?: React.ReactNode;
}

interface MoreOptionsPopoverProps {
  options: MenuOption[];
  // Default trigger (rendered in a rotated icon wrapper). Ignored when
  // `renderTrigger` is provided.
  icon?: React.ReactNode;
  minWidth?: string;
  // Make the dropdown span the full width of the trigger (matches its width).
  fullWidth?: boolean;
  // Custom trigger; receives the open state (e.g. for an animated chevron) and a
  // toggle handler. Use this to anchor the menu to an external button.
  renderTrigger?: (
    open: boolean,
    toggle: (event: React.MouseEvent) => void
  ) => React.ReactNode;
}

const MoreOptionsPopover: React.FC<MoreOptionsPopoverProps> = ({
  options,
  icon,
  minWidth = "120px",
  fullWidth = false,
  renderTrigger,
}) => {
  const theme: Theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        const target = event.target as Element;
        const isInDropdown = target?.closest('[data-dropdown="true"]');

        if (!isInDropdown) {
          handleClose();
        }
      }
    };

    if (open) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  const handleOptionClick = (option: MenuOption) => {
    option.onClick();
    handleClose();
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {renderTrigger ? (
        renderTrigger(open, handleToggle)
      ) : (
        <div style={styles.iconWrapper} onClick={handleToggle}>
          {icon}
        </div>
      )}
      {open && (
        <div
          data-dropdown="true"
          style={styles.dropdown(theme, minWidth, fullWidth)}
        >
          {options.map((option, index) => {
            const clickable = option.clickable ?? true;
            // Rich option (title + subtitle)
            if (option.subtitle !== undefined) {
              return (
                <div
                  key={index}
                  onClick={
                    clickable ? () => handleOptionClick(option) : undefined
                  }
                  style={{
                    ...styles.richOption(theme, clickable),
                    ...(clickable && hoveredIndex === index
                      ? styles.richOptionHover(theme)
                      : {}),
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div style={styles.richTitleRow}>
                    {option.titleIcon}
                    <span style={styles.richTitle(theme, option.titleMuted)}>
                      {option.label}
                    </span>
                  </div>
                  <div style={styles.richSubtitle(theme)}>
                    {option.subtitle}
                  </div>
                </div>
              );
            }
            // Plain label option (original behaviour)
            return (
              <button
                key={index}
                onClick={() => handleOptionClick(option)}
                style={{
                  ...styles.dropdownButton(theme),
                  ...(hoveredIndex === index
                    ? styles.dropdownButtonHover(theme)
                    : {}),
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MoreOptionsPopover;

const styles = {
  iconWrapper: {
    cursor: "pointer",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "rotate(90deg)",
  },
  dropdown: (theme: Theme, minWidth: string, fullWidth: boolean) => ({
    position: "absolute" as "absolute",
    top: "100%",
    right: 0,
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    zIndex: 1000,
    ...(fullWidth ? { width: "100%" } : { minWidth }),
    display: "flex",
    flexDirection: "column" as "column",
    marginTop: "4px",
    padding: fullWidth ? "8px" : "4px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxSizing: "border-box" as "border-box",
  }),
  dropdownButton: (theme: Theme) => ({
    width: "100%",
    padding: "4px 8px",
    border: "none",
    backgroundColor: "transparent",
    fontSize: "12px",
    lineHeight: "20px",
    fontFamily: "var(--font-Medium)",
    cursor: "pointer",
    textAlign: "left" as "left",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    borderRadius: "4px",
  }),
  dropdownButtonHover: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[40],
  }),
  richOption: (theme: Theme, clickable: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    padding: "8px",
    borderRadius: "8px",
    cursor: clickable ? "pointer" : "default",
  }),
  richOptionHover: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
  }),
  richTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  richTitle: (theme: Theme, muted?: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: muted ? theme.colorTokens.N[70] : theme.colorTokens.N[90],
  }),
  richSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
};
